import React from 'react';
import { Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconSparkles, IconTag } from '@tabler/icons-react';
import { QUOTE_PRICE_SOURCE, QuotePriceSource } from '../../interfaces/heavyDutyMachinery/IQuoteDraft';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { TEAL, TEAL_SOFT_BG } from '../../theme/creamyTokens';

const { Text } = Typography;

interface QuotePriceSourcePillProps {
  source?: QuotePriceSource;
}

/**
 * Says where a price came from, on the provider's own screen only.
 *
 * Deliberately never shown to the customer: "we guessed this one" is
 * information the person writing the quote needs and the person receiving it
 * must not be handed -- they are being given a price, not a confidence
 * interval. That is also why there is no pill for Manual: a price somebody
 * typed themselves needs no label.
 */
const QuotePriceSourcePill: React.FC<QuotePriceSourcePillProps> = ({ source }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  if (source !== QUOTE_PRICE_SOURCE.ESTIMATED && source !== QUOTE_PRICE_SOURCE.PROVIDER_RATE) return null;

  const estimated = source === QUOTE_PRICE_SOURCE.ESTIMATED;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        background: estimated ? token.colorWarningBg : TEAL_SOFT_BG,
        color: estimated ? token.colorWarningText : TEAL,
        whiteSpace: 'nowrap',
      }}
    >
      {estimated ? <IconSparkles size={11} /> : <IconTag size={11} />}
      <Text style={{ fontSize: 10, color: 'inherit', lineHeight: 1.4 }}>
        {translate(t, estimated ? TRANSLATIONS.QUOTE_PRICE_SOURCE_ESTIMATED : TRANSLATIONS.QUOTE_PRICE_SOURCE_PROVIDER_RATE)}
      </Text>
    </span>
  );
};

export default QuotePriceSourcePill;
